import update from 'immutability-helper'
import React from 'react'
import {Link} from 'react-router-dom'
import clsx from 'clsx'
import { makeStyles, withStyles} from 'external/material'
import Fade from '@mui/material/Fade'
import Tooltip from '@mui/material/Tooltip'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import {AssignedContentIcon, PreviewIcon} from 'resources/icons'

import {faEdit, faInfoCircle, faTrashAlt, faClone, faFolderOpen, faFolder, faCalendarAlt} from "@fortawesome/free-solid-svg-icons"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import RowSettings from './assessment-row-settings'
import DirectorySettings from './directory-row-settings'
import ShareIcon from '@mui/icons-material/Share'
import DuplicateAssessment from './duplicate-assessment'
import CreateAssignment from './create-assignment'
import EditableLabel from '../../editable-label'
import create from 'helpers/command'

const useStyles = makeStyles(bodyStyles)

export default function AssessmentsBody({context}) {

  const classes = useStyles()
  const {quizAssessments} = context
  const directories = quizAssessments.reduce((acc, assessment) => {
    if (assessment.type) {
      acc[assessment.directory] = acc[assessment.directory] || []
      acc[assessment.directory].push(assessment)
    }
    return acc
  }, {})

  const directoryCount = quizAssessments.reduce((count, {type}) => count + (type == undefined ? 1 : 0), 0)

  function handleDuplicate(result) {
    context.setState({quizAssessmentCalls: context.quizAssessmentCalls + 1})
  }

  return (<TableBody>
    <DuplicateAssessment context={context} onDuplicate={handleDuplicate} />
    <CreateAssignment context={context} />
    {quizAssessments.map(assessment => {
      return assessment.type
        ? <Assessment key={assessment.id} {...{assessment, classes, context, directoryCount}} />
        : <Directory key={assessment.id} {...{assessments: directories[assessment.id] || [], directoryId: assessment.id, classes,  context}} />
    })}
  </TableBody>
  )
}

function Directory({directoryId, context, classes, assessments}) {
  const {translate, quizSelectedDirectory} = context
  const directory = context.quizDirectories[directoryId] || {}
  const sharedCount = assessments.reduce((acc, {shared}) => acc + (shared ? 1 : 0), 0)
  const shared = assessments.length > 0 && sharedCount > 0 && sharedCount == assessments.length
  const [editingName, setEditingName] = React.useState(false)
  const opened = assessments.length > 0 && directory.opened
  const selected = directoryId == quizSelectedDirectory

  function onEditNameClick () {
    setEditingName(true)
  }

  function closeEditName() {
    setEditingName(false)
  }

  async function handleRename(newName) {
    closeEditName()
    if (directory.title != newName && newName.length > 0) {
      const [updatedId] = await create('quiz', 'renameDirectory')({id: directory.id, newName})
      if (updatedId == directory.id) {
        const quizDirectories = update(context.quizDirectories, {[updatedId]: {name: {$set: newName}}})
        context.setState({quizDirectories})
      }
    }
  }

  function toggleDirectory (e) {
    const command = selected
      ? {quizDirectories: {[directoryId]: {opened: {$apply: o => !o}}}}
      : {quizSelectedDirectory: {$set: directoryId}}
    context.updateState(command)
  }

  return (<TableRow className={clsx(classes.directoryRow)} onClick={toggleDirectory}>
      <TableCell colSpan={5} className={clsx(classes.directoryCell, classes.directoryName, {selected})} align="left" scope="row">
        <div className={classes.directoryNameContainer}>
          <div className={clsx(classes.folderIcon,{foldable: assessments.length > 0})}>
            {opened && <FontAwesomeIcon icon={faFolderOpen} />}
            {!opened && <FontAwesomeIcon icon={faFolder} />}
          </div>
          <div className={classes.directoryNameText}>
            <EditableLabel
              editing={editingName}
              label={directory.name}
              onCancel={closeEditName}
              onConfirm={handleRename}
            />
          </div>
          {sharedCount > 0 && (
          <div className={classes.sharedLink}>
            <div className={clsx(classes.sharedIcon, {shared: true})}><ShareIcon /></div>
            <div className={clsx(classes.sharedText, {selected})}>{sharedCount > 0 ? `${sharedCount}/${assessments.length}` : '-'}</div>
          </div>)}
          <div className={classes.directorySettingsLink}>
            <DirectorySettings {...{context, directory, classes, assessments, shared, onEditNameClick, selected}} />
          </div>
        </div>
      </TableCell>
  </TableRow>)
}

function Assessment ({assessment, context, classes, directoryCount}) {

  const {translate, link, navigate} = context
  const directory = context.quizDirectories[assessment.directory] || {opened: true}

  function subjectName (subject) {
    const {name = ""} =  context.mySubjects.find(({id}) => id == subject) || {}
    return name
  }

  function courseName (id) {
    const {courseName = ""} =  context.institutionCourses.find(({course}) => course == id) || {}
    return courseName
  }

  function typeName (type) {
    const {name = ""} =  context.quizTypes.find(({id}) => id == type) || {}
    return name
  }

  function handleOpenDuplicate(quizAssessmentToDuplicate) {
    const quizDialogs = update(context.quizDialogs, {'duplicate-assessment': {$set: true}})
    context.setState({quizDialogs, quizAssessmentToDuplicate})
  }


  const {validated} = assessment.properties

  function handleOpenAssign(quizAssessmentToAssign) {
    if (validated) {
      context.setState({quizAssessmentToAssign})
    }
  }


  function assessmentLink(assessment, action, sectionId) {
    return sectionId
      ? link({assessment, action, sectionId}, '/:languageId/:role/:page/:assessment/:action/:sectionId')
      : link({assessment, action}, '/:languageId/:role/:page/:assessment/:action')
  }

  function handleEditClick () {
    context.setState({quizEditFrom: '/:languageId/:role/:page/assessments', quizEditFromQuery: {...context.query}})
  }

  function handlePreviewClick () {
    context.setState({quizPreviewFrom: '/:languageId/:role/:page/assessments', quizPreviewFromQuery: {...context.query}})
  }

  const standardsLink = assessment => link({assessment, sectionName: 'standards'}, '/:languageId/:role/:page/:tab/:assessment/:sectionName', {preserveQuery: true})

  return (
        <TableRow className={clsx(classes.assessmentRow, {hidden: !directory.opened})}>
          <TableCell className={clsx(classes.cell, classes.title, {inside: directoryCount > 0 && assessment.directory != undefined})} align="left" scope="row">
              <span className={classes.titleContainer}>
                <span className={classes.assessmentIcon}><AssignedContentIcon fill={validated ? '#3295ce' : '#ff3a3a'} /></span>
                <span className={clsx(classes.titleText, {validated})}>{assessment.title}</span>
              </span>
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.subject)} align="left" scope="row">
              {subjectName(assessment.subject)}
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.course)} align="left" scope="row">
              {courseName(assessment.course)}
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.type)} align="left" scope="row">
              {translate(`${typeName(assessment.type)}-label`)}
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.actions)} align="right" scope="row">
              <div className={classes.links}>
                <Tooltip title={translate('create-assignment-tooltip')}>
                  <div onClick={() => handleOpenAssign(assessment)} className={clsx(classes.link, classes.scheduleLink, {validated})}>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </div>
                </Tooltip>
                <Tooltip title={translate('edit-assessment-tooltip')}>
                  <Link onClick={handleEditClick} to={assessmentLink(assessment.id, 'edit', assessment.firstSection)} className={clsx(classes.link, classes.editLink)}>
                    <FontAwesomeIcon icon={faEdit} />
                  </Link>
                </Tooltip>
                <Tooltip title={translate('standards-tooltip')}>
                  <Link to={standardsLink(assessment.id)} className={classes.link}>
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </Link>
                </Tooltip>
                <Tooltip title={translate('preview-tooltip')}>
                  <Link onClick={handlePreviewClick} to={assessmentLink(assessment.id, 'preview', assessment.firstSection)} className={clsx(classes.link, classes.previewLink)}>
                    <PreviewIcon />
                  </Link>
                </Tooltip>
                <Tooltip title={translate('duplicated-tooltip')}>
                  <div onClick={()=> handleOpenDuplicate(assessment)} className={clsx(classes.link, classes.duplicateLink)}>
                    <FontAwesomeIcon icon={faClone} />
                  </div>
                </Tooltip>
                <div className={classes.sharedLink}>
                  <div className={clsx(classes.sharedIcon, {shared: assessment.shared})}><ShareIcon /></div>
                  <div className={classes.sharedText}>{assessment.shared ? translate('shared') : '-'}</div>
                </div>
                <div className={classes.settingsLink}>
                  <RowSettings {...{context, assessment, classes}} />
                </div>
              </div>
            </TableCell>
        </TableRow>
  )
}


function bodyStyles (theme) {

  const verticalCentering = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  }

  return {
    assessmentRow: {
      '&:hover': {
        backgroundColor: '#def2ff'
      },
      '&.hidden': {
        display: 'none'
      }
    },
    title: {
      '&.inside': {
        paddingLeft: 25
      }
    },
    titleContainer: {
      display: 'flex'
    },
    assessmentIcon: {
      ...verticalCentering,
      marginTop: -5,
      marginRight: 15
    },
    titleText: {
      ...verticalCentering,
      color: '#ff3a3a',
      "&.validated": {
        color: '#000'
      }
    },
    links: {
      flexGrow: 1,
      display: 'flex',
      justifyContent: 'flex-end'
    },
    link: {
      color: '#009dd8',
      fontSize: '16pt',
      marginRight: 20,
      cursor: 'pointer',
      ...verticalCentering
    },
    scheduleLink: {
      color: '#c4c4c4',
      cursor: 'default',
      marginTop: 1,
      fontSize: '16pt',
      "&.validated": {
        cursor: 'pointer',
        color: '#009dd8',
      }
    },
    sharedLink: {
      display: 'flex',
      marginRight: 20,
      color: '#4d586e'
    },
    sharedIcon: {
      ...verticalCentering,
      color:'#c4c4c4', 
      marginRight: 10,
      '&.shared': {
        color: '#ea8511'
      }
    },
    sharedText: {
      ...verticalCentering,
      cursor: 'default',
      textAlign: 'center',
      marginRight: 10,
      width: 50,
      "&.selected": {
        color: "#fff"
      }
    },
    moreSettingsButton: {
      color: '#a2a6b2',
      cursor: 'pointer',
      "&.selected": {
        color: '#fff'
      }
    },
    directoryNameContainer: {
      display: 'flex'
    },
    directoryNameText: {
      marginLeft: 20,
      flexGrow: 1,
    },
    directoryCell: {
      color: '#2f75bd',
      backgroundColor: '#eff8ff',
      fontSize: '13pt',
      "&.selected": {
        backgroundColor: '#1e85d5',
        color: '#fff',
      }
    },
    folderIcon: {
      fontSize: '15pt',
      '&.foldable': {
        cursor: 'pointer'
      }
    }
  }
}

